import { useQuery } from "@tanstack/react-query";
import { Ban, Plus } from "lucide-react";
import {
  CanAccess,
  RecordContextProvider,
  useCanAccess,
  useCreate,
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
} from "ra-core";
import { useState, type KeyboardEvent } from "react";
import { DeleteButton } from "@/components/admin/delete-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import type { CrmDataProvider } from "../providers/types";
import { normaliseSuppression, type Suppression } from "./ithnkUtils";

/** Red "Do not contact" badge when any of these emails or domains is on the list */
export const DoNotContactBadge = ({
  values,
}: {
  values: (string | null | undefined)[];
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { canAccess } = useCanAccess({
    resource: "ithnk_suppressions",
    action: "list",
  });
  const list = [
    ...new Set(
      values
        .map((v) => v?.trim().toLowerCase())
        .filter((v): v is string => !!v),
    ),
  ];
  // ponytail: one is_suppressed call per value (a contact has one to three); batch it if records grow more
  const { data: suppressed } = useQuery({
    queryKey: ["ithnk_suppressions", "match", list],
    queryFn: async () =>
      (await Promise.all(list.map((v) => dataProvider.isSuppressed(v)))).some(
        Boolean,
      ),
    enabled: !!canAccess && list.length > 0,
  });
  if (!suppressed) return null;
  return (
    <Badge
      variant="destructive"
      title={translate("resources.ithnk_suppressions.badge_help")}
    >
      <Ban />
      {translate("resources.ithnk_suppressions.badge")}
    </Badge>
  );
};

/** Settings: the do-not-contact list. Admin and sales add; admin removes. */
export const SettingsSuppressions = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { data: entries = [] } = useGetList<Suppression>("ithnk_suppressions", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "created_at", order: "DESC" },
  });
  const [create, { isPending }] = useCreate();
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");

  const add = () => {
    const entry = normaliseSuppression(value);
    if (!entry) {
      notify("resources.ithnk_suppressions.invalid", { type: "warning" });
      return;
    }
    create(
      "ithnk_suppressions",
      { data: { ...entry, reason: reason.trim() || null, source: "manual" } },
      {
        onSuccess: () => {
          setValue("");
          setReason("");
        },
        onError: (error: unknown) =>
          notify(
            String((error as Error)?.message).includes("duplicate")
              ? "resources.ithnk_suppressions.already"
              : "ra.notification.http_error",
            { type: "warning" },
          ),
      },
    );
  };
  // these inputs sit inside the Settings form: Enter adds here instead of saving the settings
  const onEnter = (event: KeyboardEvent) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    add();
  };

  return (
    <Card id="do-not-contact">
      <CardContent className="space-y-4">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("resources.ithnk_suppressions.name")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("resources.ithnk_suppressions.help")}
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            aria-label={translate("resources.ithnk_suppressions.fields.value")}
            placeholder={translate(
              "resources.ithnk_suppressions.value_placeholder",
            )}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onEnter}
          />
          <Input
            aria-label={translate("resources.ithnk_suppressions.fields.reason")}
            placeholder={translate(
              "resources.ithnk_suppressions.fields.reason",
            )}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={onEnter}
          />
          <Button
            type="button"
            onClick={add}
            disabled={isPending || !value.trim()}
          >
            <Plus />
            {translate("ra.action.add")}
          </Button>
        </div>
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {translate("resources.ithnk_suppressions.empty")}
          </p>
        ) : (
          <ul className="divide-y">
            {entries.map((entry) => (
              <li key={entry.id} className="flex items-center gap-2 py-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{entry.value}</div>
                  <div className="text-xs text-muted-foreground">
                    {translate(
                      `resources.ithnk_suppressions.kinds.${entry.kind}`,
                    )}
                    {" · "}
                    {translate(
                      `resources.ithnk_suppressions.sources.${entry.source}`,
                    )}
                    {entry.reason ? ` · ${entry.reason}` : ""}
                  </div>
                </div>
                <CanAccess resource="ithnk_suppressions" action="delete">
                  <RecordContextProvider value={entry}>
                    <DeleteButton
                      resource="ithnk_suppressions"
                      redirect={false}
                      size="sm"
                    />
                  </RecordContextProvider>
                </CanAccess>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
